import { useEffect, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Slider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from '@mui/material';
import { Formik, Form, useField, useFormikContext } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useAMC } from '../context/AMCContext';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { FormikSwitch } from '../components/FormikSwitch';
import {
  removeNullOrUndefined,
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
} from '@/services';
import { Asset, useGetIpSuggestionsQuery } from '@/services/assetApi';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  // webhookUrl: yup.string().url('Must be a valid URL').optional(),
  callbackSuccess: yup.string().optional().url('Must be a valid URL'),
  callbackFailure: yup.string().url('Must be a valid URL'),
  insertDtr: yup.boolean(),
  insertAotm: yup.boolean(),
  webhookTimeout: yup
    .number()
    .min(1, 'Timeout must be at least 1 second')
    .max(60, 'Timeout cannot exceed 60 seconds')
    .nullable(),
  webhookTimeoutEnabled: yup.boolean(),
  // upiAutopayPendingHtml: yup.string().optional(),
});

type FormValues = Omit<Asset, 'id' | 'amc'> & {
  webhookTimeoutEnabled: boolean;
};

const initialValues: FormValues = {
  name: '',
  apiKey: '',
  callbackSuccess: '',
  callbackFailure: '',
  insertDtr: false,
  insertAotm: false,
  upiAutopayPendingHtml: '',
  allowedIps: '',
  webhookTimeout: 1,
  webhookTimeoutEnabled: false,
};

const WebhookTimeoutField = () => {
  const [enabledField, , enabledHelpers] = useField<boolean>('webhookTimeoutEnabled');
  const [timeoutField, timeoutMeta, timeoutHelpers] = useField<number>('webhookTimeout');
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const sliderValue = timeoutField.value ?? 1;
  const hasError = enabledField.value && !!timeoutMeta.error;

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!values.webhookUrl) {
      enabledHelpers.setValue(false);
      return;
    }
    enabledHelpers.setValue(checked);
    if (checked && (timeoutField.value === undefined || timeoutField.value < 1)) {
      timeoutHelpers.setValue(1);
    }
  };

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    const numericValue = Array.isArray(value) ? value[0] : value;
    timeoutHelpers.setValue(Math.round(numericValue));
    timeoutHelpers.setTouched(true, true);
  };

  useEffect(() => {
    if (!values.webhookUrl) {
      if (enabledField.value) {
        setFieldValue('webhookTimeoutEnabled', false, false);
      }
      if (timeoutField.value !== 1) {
        setFieldValue('webhookTimeout', 1, false);
      }
    }
  }, [values.webhookUrl, enabledField.value, timeoutField.value, setFieldValue]);

  return (
    <FormControl fullWidth error={hasError} sx={{ mt: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabledField.value && !!values.webhookUrl}
            onChange={handleToggle}
            disabled={!values.webhookUrl}
          />
        }
        label="Wait for Webhook Response"
      />
      <Box sx={{ pl: 4, pt: 1 }}>
        <Slider
          min={1}
          max={60}
          step={1}
          marks={[
            { value: 1, label: '1s' },
            { value: 60, label: '60s' },
          ]}
          value={enabledField.value && values.webhookUrl ? sliderValue : 1}
          disabled={!enabledField.value || !values.webhookUrl}
          valueLabelDisplay="auto"
          onChange={handleSliderChange}
        />
        <Typography variant="caption" color="text.secondary">
          {!values.webhookUrl
            ? 'Add a webhook URL to configure timeout'
            : enabledField.value
              ? `Maximum Webhook timeout: ${timeoutField.value} seconds`
              : 'Maximum Webhook timeout disabled'}
        </Typography>
      </Box>
      {hasError ? <FormHelperText>{timeoutMeta.error}</FormHelperText> : null}
    </FormControl>
  );
};

export default function AssetEdit({
  assetId,
  onClose,
}: {
  assetId?: string;
  onClose?: () => void;
}) {
  const { amcId } = useAMC();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !assetId;

  const { data: assets } = useGetAssetsQuery(amcId!);
  const [createAsset] = useCreateAssetMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const { data: _suggestedIps } = useGetIpSuggestionsQuery(assetId!, { skip: !assetId });

  // Find the existing asset and convert it to the Asset type from assetApi
  let existingAsset = assets?.find((asset) => asset.id === assetId);
  if (existingAsset) {
    // Add required fields that might be missing in the amcApi.Asset interface
    const assetWithRequiredFields = {
      ...removeNullOrUndefined(existingAsset),
      apiKey: existingAsset.apiKey || '',
      webhookTimeout: existingAsset.webhookTimeout ?? 0,
    };
    existingAsset = assetWithRequiredFields as Asset;
  }

  const formInitialValues: FormValues = existingAsset
    ? (() => {
      const { amc: _amc, id: _id, webhookTimeout, ...rest } = existingAsset;
      const sanitizedTimeout = webhookTimeout && webhookTimeout >= 1 ? Math.min(webhookTimeout, 60) : 1;
      const hasWebhookUrl = !!rest.webhookUrl;
      return {
        ...rest,
        webhookTimeout: sanitizedTimeout,
        webhookTimeoutEnabled: !!webhookTimeout && webhookTimeout > 0 && hasWebhookUrl,
      };
    })()
    : initialValues;

  const handleSubmit = async (values: FormValues) => {
    try {
      const { webhookTimeoutEnabled, webhookTimeout, ...restValues } = values;
      const payload = {
        ...restValues,
        webhookTimeout: webhookTimeoutEnabled ? webhookTimeout ?? 1 : 0,
      };

      if (isNew) {
        await createAsset({
          ...payload,
          apiKey: payload.apiKey || Math.random().toString(36).substring(2, 15), // Generate a random API key if not provided
          amc: { id: amcId! },
        }).unwrap();
      } else {
        await updateAsset({
          ...payload,
          id: assetId!,
          amc: { id: amcId! },
        }).unwrap();
      }
      enqueueSnackbar('Asset saved successfully', { variant: 'success' });
      navigate(`/amcs/${amcId}/assets`);
      onClose?.();
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to save asset', { variant: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 4,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
              <FormikTextField name="name" label="Name" fullWidth />
              {!isNew ? <FormikTextField name="apiKey" label="API Key" fullWidth /> : <></>}
              <FormikTextField name="webhookUrl" label="Webhook URL" fullWidth />
              <WebhookTimeoutField />
              <FormikTextField name="callbackSuccess" label="Success Callback URL" fullWidth />
              <FormikTextField name="callbackFailure" label="Failure Callback URL" fullWidth />
              <FormikTextField name="proxyUrl" label="Proxy URL" fullWidth />
              <FormikTextField name="allowedIps" label="Allowed IP Addresses" fullWidth />
              <Typography variant="body2" color="text.secondary">
                Recent IPs: {Array.isArray(_suggestedIps) && _suggestedIps.length > 0
                  ? _suggestedIps.join(", ") : "No Suggestions"}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormikSwitch name="insertDtr" label="Housekeeping" />
                <FormikSwitch name="insertAotm" label="Insert Mandate" />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <FormikTextField
                multiline
                rows={15}
                name="upiAutoPayPendingHtml"
                label="UPI Autopay Pending HTML"
                fullWidth
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: isMobile ? 2 : 0,
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{ borderRadius: '20px', width: isMobile ? '100%' : 'auto' }}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disableElevation
                  variant="contained"
                  sx={{ borderRadius: '20px', width: isMobile ? '100%' : 'auto' }}
                >
                  {isNew ? 'Create Asset' : 'Update Asset'}
                </FormikSubmitButton>
              </Box>
            </Box>
          </Box>
        </Form>
      </Formik>
    </Box>
  );
}
